import { forwardRef, useEffect, useRef, useState } from "react";
import { Check, Lock, Search, CheckSquare, Phone, Users, Mail, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BottomSheet } from "@/components/shared/PagePrimitives";
import { Kbd } from "@/components/shared/ListNav";

// 361: vlastnosti úkolu v detailu podle Linearu — řádky Stav · Priorita · Termín · Kdo · Zakázka · Typ · Zadal
// s klávesou vpravo (S P D A Z T). Klik nebo klávesa otevře výběr přímo na místě: na počítači bublina
// pod řádkem, na telefonu okno zespodu. Výběr ovládá ↑ ↓ Enter, čísla 1–9, Esc zavře.
export const TYPE_ICON = { task: CheckSquare, call: Phone, meeting: Users, email: Mail };
export const TYPE_LABEL = { task: "Úkol", call: "Hovor", meeting: "Schůzka", email: "E-mail" };
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Stav jako kroužek (Linear): Nový čárkovaně, V řešení napůl, Ke schválení ze tří čtvrtin, Hotovo zeleně
export function StatusIcon({ status, size = 16 }) {
  const s = { width: size, height: size };
  if (status === "Schváleno") return <svg viewBox="0 0 16 16" style={s} className="shrink-0" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="hsl(var(--success))" /><path d="m5 8.2 2 2 4-4.2" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  if (status === "Hotovo") return <svg viewBox="0 0 16 16" style={s} className="shrink-0" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="#f59e0b" strokeWidth="1.6" /><path d="M8 4a4 4 0 1 1-4 4h4z" fill="#f59e0b" /></svg>;
  if (status === "V řešení") return <svg viewBox="0 0 16 16" style={s} className="shrink-0" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="hsl(var(--accent-orange))" strokeWidth="1.6" /><path d="M8 4a4 4 0 0 1 0 8z" fill="hsl(var(--accent-orange))" /></svg>;
  return <svg viewBox="0 0 16 16" style={s} className="shrink-0 text-muted-foreground" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.3 2.1" /></svg>;
}
const PRIO_N = { "Vysoká": 3, "Střední": 2, "Nízká": 1 };
const PRIO_CLS = { "Vysoká": "text-destructive", "Střední": "text-blue-500 dark:text-blue-400", "Nízká": "text-muted-foreground" };
export function PrioIcon({ value }) {
  const n = PRIO_N[value] || 2;
  return (
    <svg viewBox="0 0 16 16" className={`w-4 h-4 shrink-0 ${PRIO_CLS[value] || ""}`} aria-hidden="true">
      {[0, 1, 2].map((i) => <rect key={i} x={2 + i * 4.6} y={11 - i * 3.2} width="3" height={3 + i * 3.2} rx="1" fill="currentColor" opacity={i < n ? 1 : 0.22} />)}
    </svg>
  );
}

// Řádek vlastnosti. ro = jen ke čtení (zámek a důvod v bublině), hint = klávesa (jen na počítači).
export const PropRow = forwardRef(function PropRow({ label, hint, ro, roTitle, testid, mobile, children, ...rest }, ref) {
  const cls = `group/prop w-full grid grid-cols-[96px_minmax(0,1fr)_auto] items-center gap-2 text-left
    ${mobile ? "min-h-[50px] px-0.5 border-b border-border/60" : "min-h-[40px] px-2 rounded-[10px]"}`;
  if (ro) {
    return (
      <div className={cls} title={roTitle} data-testid={testid} data-ro="true" onClick={rest.onClick}>
        <span className="text-[13.5px] text-muted-foreground">{label}</span>
        <span className="min-w-0 flex items-center gap-2 text-[14px] font-semibold">{children}</span>
        <Lock size={13} className={`text-muted-foreground ${mobile ? "opacity-60" : "opacity-0 group-hover/prop:opacity-70"}`} aria-hidden="true" />
      </div>
    );
  }
  return (
    <button ref={ref} type="button" data-testid={testid} title={hint ? `${label} (${hint})` : label} {...rest}
      className={`${cls} hover:bg-secondary/60 data-[state=open]:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40`}>
      <span className="text-[13.5px] text-muted-foreground">{label}</span>
      <span className="min-w-0 flex items-center gap-2 text-[14px] font-semibold">{children}</span>
      {mobile ? <ChevronRight size={16} className="text-muted-foreground" /> : hint ? <Kbd className="opacity-70 group-hover/prop:opacity-100">{hint}</Kbd> : <span />}
    </button>
  );
});

// Bublina (počítač) nebo okno zespodu (telefon) s výběrem; trigger = PropRow.
export function PropPicker({ open, onOpenChange, trigger, title, mobile, width = 290, testid, children }) {
  if (mobile) return (<>{trigger}<BottomSheet open={open} onOpenChange={onOpenChange} title={title} testid={testid}>{open && children}</BottomSheet></>);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} collisionPadding={12} className="p-1.5 rounded-[14px]" style={{ width }} data-testid={testid}
        onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
        {open && children}
      </PopoverContent>
    </Popover>
  );
}

const Box = ({ on }) => (
  <span className={`w-[18px] h-[18px] rounded-[5px] border-[1.5px] grid place-items-center shrink-0 ${on ? "bg-brand border-brand text-white" : "border-muted-foreground/60"}`}>{on && <Check size={13} strokeWidth={3} />}</span>
);

// options = [{ value, label, icon?, sub?, why? (nejde — proč), on?, key? (číslo), right? }]
// multi = zaškrtávání (Kdo): Enter / klik přepne a výběr zůstane otevřený.
export function OptionList({ options, onPick, search, placeholder = "Hledat…", multi, mobile, footer, testid }) {
  const [q, setQ] = useState("");
  const [act, setAct] = useState(() => (multi ? 0 : Math.max(0, options.findIndex((o) => o.on))));
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const shown = search ? options.filter((o) => !q || norm(`${o.label} ${o.sub || ""}`).includes(norm(q))) : options;
  useEffect(() => { if (!mobile) (search ? inputRef.current : listRef.current)?.focus({ preventScroll: true }); }, []); // eslint-disable-line
  useEffect(() => { listRef.current?.querySelector('[data-cur="true"]')?.scrollIntoView?.({ block: "nearest" }); }, [act]);
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); e.stopPropagation(); setAct((a) => Math.min(shown.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); e.stopPropagation(); setAct((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); if (shown[act]) onPick(shown[act]); }
    else if (!search && /^[1-9]$/.test(e.key)) { const o = shown.find((x) => x.key === e.key); if (o) { e.preventDefault(); onPick(o); } }
  };
  return (
    <div onKeyDown={onKey} data-testid={testid}>
      {search && (
        <div className="h-10 mb-1.5 rounded-[11px] bg-secondary flex items-center gap-2 px-3">
          <Search size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
          <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setAct(0); }} placeholder={placeholder} aria-label={placeholder}
            className="bg-transparent outline-none w-full min-w-0 text-base md:text-sm" data-testid={`${testid}-search`} />
        </div>
      )}
      <ul ref={listRef} tabIndex={-1} role="listbox" aria-multiselectable={multi || undefined} className={`outline-none overflow-y-auto ${mobile ? "" : "max-h-[300px]"}`}>
        {shown.map((o, i) => (
          <li key={String(o.value)}>
            <button type="button" role="option" aria-selected={!!o.on} aria-disabled={o.why ? "true" : undefined} data-cur={i === act} onMouseEnter={() => setAct(i)}
              onClick={() => onPick(o)} data-testid={`${testid}-opt-${o.value ?? "none"}`}
              className={`w-full flex items-center gap-2.5 px-2.5 rounded-[9px] text-left ${mobile ? "min-h-[52px] text-[15.5px]" : "min-h-[38px] text-sm"}
                ${i === act ? "bg-secondary" : ""} ${o.why ? "opacity-50 cursor-not-allowed" : ""}`}>
              {multi && <Box on={o.on} />}
              {o.icon}
              <span className="flex-1 min-w-0">
                <span className="block truncate">{o.label}</span>
                {(o.why || o.sub) && <span className="block text-xs text-muted-foreground truncate">{o.why || o.sub}</span>}
              </span>
              {o.right}
              {!multi && o.on && <Check size={16} className="text-brand shrink-0" aria-hidden="true" />}
              {!multi && !o.on && o.key && !mobile && <Kbd>{o.key}</Kbd>}
            </button>
          </li>
        ))}
        {!shown.length && <li className="px-3 py-3 text-sm text-muted-foreground">Nic nenalezeno</li>}
      </ul>
      {footer}
    </div>
  );
}
